import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Typography, Button, Container, Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useLocalizedPath } from '../../hooks/useLocalizedPath';
import { colors } from '../../theme/colorTokens';
import { REFERRALS } from '../shared/ReferralCTA';
import { gtmOfferClick } from '../../utils/gtm';
const wealthsimpleLogo = '/logos/wealthsimple.svg';
const tangerineLogo = '/logos/tangerine-icon.webp';
const borrowellLogo = '/logos/borrowell-icon.webp';

const HeroSection = () => {
  const { t } = useTranslation();
  const lp = useLocalizedPath();

  const topPicks = [
    {
      name: 'Wealthsimple',
      logo: wealthsimpleLogo,
      feature: t('home.hero.pick1Feature'),
      ctaLabel: t('home.hero.viewOffer'),
      href: REFERRALS.wealthsimple.href,
      bank: 'wealthsimple',
    },
    {
      name: 'Tangerine',
      logo: tangerineLogo,
      feature: t('home.hero.pick2Feature'),
      ctaLabel: t('home.hero.viewOffer'),
      href: REFERRALS.tangerine.href,
      bank: 'tangerine',
    },
    {
      name: 'Borrowell',
      logo: borrowellLogo,
      feature: t('home.hero.pick3Feature'),
      ctaLabel: t('home.hero.viewOffer'),
      href: REFERRALS.borrowell.href,
      bank: 'borrowell',
    },
  ];

  const secondaryLinks = [
    { label: t('home.hero.link1'), routeKey: 'blogCredito' },
    { label: t('home.hero.link2'), routeKey: 'banking' },
    { label: t('home.hero.link3'), routeKey: 'mortgages' },
    { label: t('home.hero.link4'), routeKey: 'taxCalendar' },
  ];

  return (
    <Box
      component="section"
      sx={{
        background: 'linear-gradient(135deg, #0f1f3a 0%, #1b2e4f 100%)',
        minHeight: { xs: 'auto', md: '520px' },
      }}
    >
      <Container maxWidth="lg">
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'stretch', md: 'flex-start' },
            gap: { xs: 3, md: 6 },
            py: { xs: 5, md: 10 },
          }}
        >
          {/* ── LEFT COLUMN (58%) ── */}
          <Box sx={{ flex: { md: '0 0 58%' }, maxWidth: { md: '58%' }, width: '100%' }}>
            <Typography
              component="h1"
              sx={{
                fontWeight: 700,
                color: colors.bgCard,
                fontSize: { xs: '2.25rem', md: '3.75rem' },
                lineHeight: 1.1,
                letterSpacing: '-0.03em',
              }}
            >
              {t('home.hero.title')}
            </Typography>

            <Typography
              sx={{
                color: 'rgba(255,255,255,0.75)',
                fontSize: '18px',
                lineHeight: 1.5,
                mt: 3,
                maxWidth: 520,
              }}
            >
              {t('home.hero.subtitle')}
            </Typography>

            {/* Primary CTA */}
            <Button
              component={RouterLink}
              to={lp('getStarted')}
              variant="contained"
              disableElevation
              sx={{
                mt: 4,
                bgcolor: '#fff',
                color: '#0f1f3a',
                height: 54,
                px: { xs: 4, md: 7 },
                fontWeight: 700,
                borderRadius: '12px',
                boxShadow: '0 6px 24px rgba(255,255,255,0.15), 0 4px 20px rgba(0,0,0,0.25)',
                textTransform: 'none',
                fontSize: { xs: 16, md: 17 },
                width: { xs: '100%', sm: 'auto' },
                '&:hover': { bgcolor: '#f0f0f0', boxShadow: '0 8px 32px rgba(255,255,255,0.2), 0 6px 24px rgba(0,0,0,0.3)' },
              }}
            >
              {t('home.hero.cta')}
            </Button>

            {/* Secondary links row */}
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 0,
                mt: 3,
              }}
            >
              {secondaryLinks.map((link, i) => (
                <React.Fragment key={link.routeKey}>
                  {i > 0 && (
                    <Box
                      component="span"
                      sx={{
                        mx: 1,
                        color: 'rgba(255,255,255,0.25)',
                        fontSize: '14px',
                        userSelect: 'none',
                      }}
                    >
                      ·
                    </Box>
                  )}
                  <Typography
                    component={RouterLink}
                    to={lp(link.routeKey)}
                    sx={{
                      color: 'rgba(255,255,255,0.4)',
                      fontSize: '13px',
                      textDecoration: 'none',
                      transition: 'color 0.2s',
                      py: { xs: 0.75, md: 0 },
                      '&:hover': { color: 'rgba(255,255,255,0.8)' },
                    }}
                  >
                    {link.label}
                  </Typography>
                </React.Fragment>
              ))}
            </Box>
          </Box>

          {/* ── RIGHT COLUMN (42%) ── */}
          <Box
            sx={{
              flex: { md: '0 0 38%' },
              maxWidth: { md: '38%' },
              width: '100%',
            }}
          >
            {/* Overline */}
            <Typography
              sx={{
                fontSize: '11px',
                textTransform: 'uppercase',
                letterSpacing: 2,
                color: 'rgba(255,255,255,0.35)',
                mb: 0.75,
              }}
            >
              {t('home.hero.overline')}
            </Typography>

            {/* Title */}
            <Typography
              sx={{
                fontSize: '14px',
                color: 'rgba(255,255,255,0.65)',
                fontWeight: 500,
                mb: 2,
              }}
            >
              {t('home.hero.topPicksTitle')}
            </Typography>

            {/* Product cards */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {topPicks.map((pick) => (
                <Box
                  key={pick.name}
                  sx={{
                    background: 'rgba(255,255,255,0.04)',
                    border: '1px solid rgba(255,255,255,0.06)',
                    borderRadius: '10px',
                    p: '12px 16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    transition: 'background 0.3s, transform 0.3s',
                    '&:hover': {
                      background: 'rgba(255,255,255,0.06)',
                    },
                  }}
                >
                  {/* Logo */}
                  <Box
                    component="img"
                    src={pick.logo}
                    alt={pick.name}
                    width={36}
                    height={36}
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: '50%',
                      objectFit: 'cover',
                      flexShrink: 0,
                      border: '1px solid rgba(255,255,255,0.15)',
                    }}
                  />

                  {/* Text column */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: '14px',
                        fontWeight: 600,
                        color: 'rgba(255,255,255,0.9)',
                        lineHeight: 1.3,
                      }}
                    >
                      {pick.name}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '13px',
                        color: 'rgba(255,255,255,0.45)',
                        lineHeight: 1.4,
                      }}
                    >
                      {pick.feature}
                    </Typography>
                  </Box>

                  {/* CTA link → referral */}
                  <Typography
                    component="a"
                    href={pick.href}
                    target="_blank"
                    rel="noopener noreferrer sponsored"
                    onClick={() => {
                      if (window.plausible) window.plausible('Referral Click', { props: { bank: pick.bank } });
                      gtmOfferClick({ product: 'referral', provider: pick.bank, vertical: 'hero', url: pick.href });
                    }}
                    sx={{
                      fontSize: '13px',
                      color: 'rgba(255,255,255,0.55)',
                      textDecoration: 'none',
                      flexShrink: 0,
                      whiteSpace: 'nowrap',
                      transition: 'color 0.2s',
                      py: 1,
                      px: 1.5,
                      minHeight: 44,
                      display: 'inline-flex',
                      alignItems: 'center',
                      '&:hover': { color: colors.bgCard },
                    }}
                  >
                    {pick.ctaLabel}
                  </Typography>
                </Box>
              ))}
            </Box>

            {/* Disclosure link */}
            <Typography
              component={RouterLink}
              to={lp('howWeMonetize')}
              sx={{
                display: 'inline-block',
                fontSize: '12px',
                color: 'rgba(255,255,255,0.35)',
                textDecoration: 'none',
                mt: 2,
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              {t('home.hero.disclosure')}
            </Typography>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default HeroSection;
